"use client"

import { fetchPostByCategories } from "@/services/postServices"
import { useCallback, useEffect, useState } from "react"

interface UseWebsiteCategoriesOptions {
    websiteDomain?: string
    locale?: string
}

interface WebsiteCategory {
    id: number
    name: string
    slug: string
}

export const useWebsiteCategories = (options: UseWebsiteCategoriesOptions = {}) => {
    const { websiteDomain, locale = "vi" } = options

    const [categories, setCategories] = useState<WebsiteCategory[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const fetchWebsiteCategories = useCallback(async () => {
        if (!websiteDomain) {
            setCategories([])
            return
        }

        setLoading(true)
        setError(null)

        try {
            // Use the anonymous public service. Importing the admin post
            // service here attached the admin token and forced Strapi's Redis
            // cache plugin to bypass this public post-list request.
            const response = await fetchPostByCategories([], {
                domain: websiteDomain,
                locale,
                page: "1",
                pageSize: "100",
            })

            if (response?.data && Array.isArray(response.data)) {
                const categoryMap = new Map<number, WebsiteCategory>()

                for (const post of response.data) {
                    const postCategories = post.attributes.categories?.data || []
                    for (const cat of postCategories) {
                        if (!categoryMap.has(cat.id)) {
                            categoryMap.set(cat.id, {
                                id: cat.id,
                                name: cat.attributes.name || "",
                                slug: cat.attributes.slug || "",
                            })
                        }
                    }
                }

                const uniqueCategories = Array.from(categoryMap.values()).sort((a, b) =>
                    a.name.localeCompare(b.name)
                )
                setCategories(uniqueCategories)
            } else {
                setCategories([])
            }
        } catch (err) {
            console.error("Failed to fetch website categories:", err)
            setError(err instanceof Error ? err.message : "Failed to fetch categories")
            setCategories([])
        } finally {
            setLoading(false)
        }
    }, [websiteDomain, locale])

    useEffect(() => {
        fetchWebsiteCategories()
    }, [fetchWebsiteCategories])

    return {
        categories,
        loading,
        error,
        refetch: fetchWebsiteCategories,
    }
}
